import React, { useState, useEffect, useRef } from 'react';
import PlotData from '../spectralLibraryHomePage/components/SpectralPlotter/PlotData';
import InstrumentListInput from '../spectralLibraryHomePage/components/InstrumentListInput/InstrumentListInput';
import InstrumentVariedInput from '../spectralLibraryHomePage/components/InstrumentVariedInput/InstrumentVariedInput';
import MetaData from '../spectralLibraryHomePage/components/MetaData/MetaData';
import Button from '../../Components/Functionality/buttons/Buttons';
import { Navigate, useNavigate } from 'react-router-dom';
import * as Papa from 'papaparse';
import AnimatedLinePlaceholder from './Components/AnimatedPlot';
import { ref, uploadBytes, getDownloadURL, deleteObject, updateMetadata, getMetadata } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { fbStorage, fbFunctions } from '../../firebase/firebase'
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import domtoimage from 'dom-to-image';
import { useGA4React } from 'ga-4-react';

const AnalyseTab = () => {
  const [activeTab, setActiveTab] = useState('Correlation Match');
  const [showAnalysisForm, setShowAnalysisForm] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState('');
  const [variableInputValue, setVariableInputValue] = useState('');
  const [selectedMetaDropName, setSelectedMetaDropName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [aiMatchesUsed, setAiMatchesUsed] = useState(1); // Track AI matches used
  const [sessionResults, setSessionResults] = useState([]); // Store current session results
  const [plotConfig, setPlotConfig] = useState(null);
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const navigate = useNavigate();
  const [metadataFields, setMetadataFields] = useState({
    inputOneValue: '',
    inputTwoValue: '',
    inputThreeValue: '',
    inputFourValue: '',
  });
  // Add new state variables for chemometrics functionality
  const [formInstrument, setFormInstrument] = useState('');
  const [formVariable, setFormVariable] = useState('');   
  const [fileCode, setFileCode] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [predictionAcc, setPredictionAcc] = useState('');
  const [predictedPolymer, setPredictedPolymer] = useState('');
  const [showRightColumn, setShowRightColumn] = useState(false);
  // Existing state variables
  const [ftirData, setFtirData] = useState([]);
  const [ramanData, setRamanData] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [filters, setFilters] = useState({});
  const [showFilterMenu, setShowFilterMenu] = useState(null);
  const filterMenuRef = useRef(null);
  const ga4 = useGA4React();
  useEffect(() => {
    trackAnalyseTabView();
    trackAnalysisDeviceUsage(
      isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop', 
      windowWidth
    );
  }, []);
  // Analytics functions for AnalyseTab component
  // Add these to your component and call them at appropriate times

  // 1. Page/tab view tracking
  const trackAnalyseTabView = () => {
    if (ga4) {
    ga4.event("analyse_tab_viewed", {
      page_title: "Spectral Analysis",
      timestamp: new Date().toISOString()
    });
  }
  };


  // 3. Analysis form interactions
  const trackAnalysisFormShow = () => {
    if (ga4) {
    ga4.event("analysis_form_opened", {
      timestamp: new Date().toISOString()
    });
  }
  };

  // 4. Instrument and variable selection
  const trackAnalysisInstrumentSelection = (instrumentType) => {
    if (ga4) {
    ga4.event("analysis_instrument_selected", {
      instrument_type: instrumentType,
      timestamp: new Date().toISOString()
    });
  }
  };

  const trackAnalysisVariableSelection = (variable, instrumentType) => {
    if (ga4) {
    ga4.event("analysis_variable_selected", {
      variable: variable,
      instrument_type: instrumentType,
      timestamp: new Date().toISOString()
    });
  }
  };

  // 5. File upload tracking
  const trackAnalysisFileUpload = (fileName, fileSize) => {
    if (ga4) {
    ga4.event("analysis_file_uploaded", {
      file_name: fileName,
      file_size_kb: Math.round(fileSize / 1024),
      timestamp: new Date().toISOString()
    });
  }
  };

  // 6. Analysis execution tracking
  const trackAnalysisStart = (instrument, variable, tabType) => {
    if (ga4) {
    ga4.event("analysis_started", {
      instrument_type: instrument,
      variable: variable,
      tab_type: tabType, // 'Correlation Match' or 'AI Match'
      timestamp: new Date().toISOString()
    });
  }
  };

  const trackAnalysisComplete = (result, processingTime, tabType) => {
    if (ga4) {
    ga4.event("analysis_completed", {
      predicted_polymer: result.best_match,
      confidence_score: Math.round(result.predictionCert * 100),
      ml_confidence: Math.round(result.ml_confidence * 100),
      corr_confidence: Math.round(result.corr_confidence * 100),
      processing_time_ms: processingTime,
      tab_type: tabType,
      timestamp: new Date().toISOString()
    });
  }
  };

  const trackAnalysisError = (errorMessage, stage, tabType) => {
    if (ga4) {
    ga4.event("analysis_error", {
      error_message: errorMessage,
      error_stage: stage, // 'upload', 'processing', 'display'
      tab_type: tabType,
      timestamp: new Date().toISOString()
    });
  }
  };

  // 7. Results interaction tracking
  const trackResultViewed = (resultId, polymer, confidence) => {
    if (ga4) {
    ga4.event("analysis_result_viewed", {
      result_id: resultId,
      polymer_type: polymer,
      confidence_score: confidence,
      timestamp: new Date().toISOString()
    });
  }
  };

  const trackResultExport = (exportType, resultCount) => {
    if (ga4) {
    ga4.event("analysis_results_exported", {
      export_type: exportType, // 'csv', 'plot_png'
      result_count: resultCount,
      timestamp: new Date().toISOString()
    });
  }
  };

  // 8. Filter usage tracking
  const trackFilterApplied = (filterType, filterValue) => {
    if (ga4) {
    ga4.event("analysis_filter_applied", {
      filter_type: filterType, // 'polymer', 'confidence', 'classification'
      filter_value: filterValue,
      timestamp: new Date().toISOString()
    });
  }
  };

  const trackFilterCleared = (filterType) => {
    if (ga4) {
    ga4.event("analysis_filter_cleared", {
      filter_type: filterType,
      timestamp: new Date().toISOString()
    });
  }
  };

  // 10. Device usage tracking
  const trackAnalysisDeviceUsage = (deviceType, windowWidth) => {
    if (ga4) {
    ga4.event("analysis_device_usage", {
      device_type: deviceType, // 'mobile', 'tablet', 'desktop'
      window_width: windowWidth,
      is_mobile_blocked: deviceType === 'mobile'
    });
  }
  };

  // 11. Authentication tracking
  const trackLoginPrompt = (context) => {
    if (ga4) {
    ga4.event("analysis_login_prompted", {
      prompt_context: context, // 'save_results', 'unlimited_access'
      timestamp: new Date().toISOString()
    });
  }
  };

  const generateRandomFileCode = (length) => {
    let charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let code = '';
    for (let i = 0; i < length; i++) {
      let randomIndex = Math.floor(Math.random() * charset.length);
      code += charset.charAt(randomIndex);
    }
    return code;
  };

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    const loadSpectralData = async () => {
      try {
        // Load FTIR data
        const ftirResponse = await window.fs.readFile('Fourier Transform Infrared Spectroscopy-Polystyrene-Reflectance-CfYfskEM.txt', { encoding: 'utf8' });
        const ftirParsed = Papa.parse(ftirResponse, { 
          delimiter: '\t',
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true
        });
        
        // Load Raman data
        const ramanResponse = await window.fs.readFile('Raman Microscopy-Polystyrene-532-0yD6cV6e.txt', { encoding: 'utf8' });
        const ramanParsed = Papa.parse(ramanResponse, { 
          delimiter: '\t',
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true
        });
        
        setFtirData(ftirParsed.data);
        setRamanData(ramanParsed.data);
      } catch (error) {
        console.error("Error loading spectral data:", error);
      }
    };
    
    loadSpectralData();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target)) {
        setShowFilterMenu(null);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  // 4. Add this handler function for the Export button
  const handleExportToCSV = () => {
    trackResultExport('csv', sessionResults.length);
    setIsExporting(true);
    const resultsToExport = sessionResults.length > 0 ? sessionResults : analysisResults;
    
    // If using the utility functions:
    // exportResultsToCSV(resultsToExport, ftirData, ramanData);
    
    // If implementing directly:
    try {
      // Extract wavelengths from the FTIR and Raman data
      const ftirWavelengths = ftirData
        .map(item => Math.round(item.Wavelengths))
        .filter(wl => wl >= 1250 && wl <= 3600)
        .sort((a, b) => b - a); // Sort in descending order
      
      const ramanWavelengths = ramanData
        .map(item => Math.round(item.Wavelengths))
        .filter(wl => wl >= 250 && wl <= 3300)
        .sort((a, b) => a - b); // Sort in ascending order
      
      // Create column headers
      const basicColumns = [
        "Image", 
        "Predicted Polymer", 
        "Prediction confidence", 
        "Classification", 
        "Length (µm)", 
        "Width (µm)", 
        "Additional info"
      ];
      
      // Add FTIR wavelengths
      const ftirColumns = ftirWavelengths.map(wl => `FTIR_${wl}`);
      
      // Add Raman wavelengths
      const ramanColumns = ramanWavelengths.map(wl => `Raman_${wl}`);
      
      // Combine all column headers
      const allColumns = [...basicColumns, ...ftirColumns, ...ramanColumns];
      
      // Create rows with spectral data for each item
      const dataWithSpectra = resultsToExport.map(item => {
        const row = {
          "Image": `Image_${item.id || ''}`,
          "Predicted Polymer": item.polymer || '',
          "Prediction confidence": item.confidence || '',
          "Classification": item.classification || '',
          "Length (µm)": item.length || '',
          "Width (µm)": item.width || '',
          "Additional info": item.additionalInfo || 'Sample data'
        };
        
        // Add FTIR data to each row
        ftirWavelengths.forEach(wl => {
          const ftirItem = ftirData.find(item => Math.round(item.Wavelengths) === wl);
          row[`FTIR_${wl}`] = ftirItem ? ftirItem.Intensity : 0;
        });
        
        // Add Raman data to each row
        ramanWavelengths.forEach(wl => {
          const ramanItem = ramanData.find(item => Math.round(item.Wavelengths) === wl);
          row[`Raman_${wl}`] = ramanItem ? ramanItem.Intensity : 0;
        });
        
        return row;
      });
      
      // Generate CSV content
      const csv = Papa.unparse({
        fields: allColumns,
        data: dataWithSpectra
      });
      
      // Create and download the file
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'polymer_analysis_results.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error exporting CSV:", error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownloadPlotWithBranding = async (result) => {
    try {
      trackResultExport('plot_png', 1);
      toast.info('Generating high-resolution export...', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: false,
        toastId: 'exporting-plot',
      });

      // High DPI settings
      const DPI_SCALE = 3; // 3x resolution for crisp output
      const BASE_WIDTH = 1400;
      const BASE_HEIGHT = 2000;

      // Step 1: Capture the plot at high resolution
      let plotDataUrl = null;
      const plotContainer = document.getElementById('plot-section');
      const plotArea = plotContainer?.querySelector('[data-plot-area]') || 
                      plotContainer?.querySelector('.recharts-wrapper') ||
                      plotContainer?.querySelector('canvas') ||
                      plotContainer?.querySelector('svg');

      if (plotArea) {
        try {
          // Capture plot at very high resolution
          plotDataUrl = await domtoimage.toPng(plotArea, {
            quality: 1.0,
            bgcolor: '#ffffff',
            cacheBust: true,
            pixelRatio: DPI_SCALE, // 3x resolution
            width: plotArea.offsetWidth * DPI_SCALE,
            height: plotArea.offsetHeight * DPI_SCALE,
            style: {
              transform: `scale(${DPI_SCALE})`,
              transformOrigin: 'top left',
              width: plotArea.offsetWidth + 'px',
              height: plotArea.offsetHeight + 'px'
            }
          });
          // console.log'High-res plot captured successfully');
        } catch (plotError) {
          console.warn('Failed to capture plot:', plotError);
        }
      }

      // Step 2: Create high-DPI canvas
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set actual canvas size (high resolution)
      canvas.width = BASE_WIDTH * DPI_SCALE;
      canvas.height = BASE_HEIGHT * DPI_SCALE;
      
      // Scale context to match
      ctx.scale(DPI_SCALE, DPI_SCALE);
      
      // Set display size (CSS pixels)
      canvas.style.width = BASE_WIDTH + 'px';
      canvas.style.height = BASE_HEIGHT + 'px';

      // Enable font smoothing
      ctx.textRenderingOptimization = 'optimizeQuality';
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Fill background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, BASE_WIDTH, BASE_HEIGHT);

      let yPosition = 60;

      // Header section with crisp rendering
      ctx.fillStyle = '#4F46E5';
      ctx.fillRect(40, 40, 50, 50);
      
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('PA', 65, 70);

      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 24px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Spectral Analysis Report', 110, 65);

      ctx.fillStyle = '#666666';
      ctx.font = '14px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.fillText('plasticanalytics.co.uk', 110, 85);

      // Date and Result ID (right aligned)
      ctx.textAlign = 'right';
      ctx.fillText(`Generated: ${new Date().toLocaleString()}`, BASE_WIDTH - 40, 65);
      ctx.fillText(`Result ID: ${result?.id || 'N/A'}`, BASE_WIDTH - 40, 85);

      // Draw crisp line separator
      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(40, 120);
      ctx.lineTo(BASE_WIDTH - 40, 120);
      ctx.stroke();

      yPosition = 160;

      // Match Details Section
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Match Details', 40, yPosition);

      yPosition += 40;

      // Details box with crisp borders
      const detailsBoxHeight = 200;
      ctx.fillStyle = '#f9fafb';
      ctx.fillRect(40, yPosition, BASE_WIDTH - 80, detailsBoxHeight);

      ctx.strokeStyle = '#e0e0e0';
      ctx.lineWidth = 1;
      ctx.strokeRect(40, yPosition, BASE_WIDTH - 80, detailsBoxHeight);

      // Details content with high-quality fonts
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      
      yPosition += 35;
      ctx.fillText('Predicted Polymer:', 60, yPosition);
      ctx.fillStyle = polymer_colors[result?.polymer]?.text || '#333';
      ctx.font = '15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.fillText(result?.polymer || 'N/A', 220, yPosition);

      yPosition += 35;
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.fillText('Overall Confidence:', 60, yPosition);
      ctx.fillStyle = getConfidenceColor(result?.confidence || '0%');
      ctx.font = 'bold 18px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.fillText(result?.confidence || 'N/A', 220, yPosition);

      yPosition += 35;
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.fillText('Classification:', 60, yPosition);
      ctx.fillStyle = '#666666';
      ctx.font = '15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.fillText(result?.classification || 'N/A', 220, yPosition);

      yPosition += 35;
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.fillText('Instrument:', 60, yPosition);
      ctx.fillStyle = '#666666';
      ctx.font = '15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.fillText(`${result?.instrument || 'N/A'} (${result?.variable || 'N/A'})`, 220, yPosition);

      // Confidence breakdown with better typography
      if (result?.analysisData) {
        yPosition += 50;
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(60, yPosition);
        ctx.lineTo(BASE_WIDTH - 60, yPosition);
        ctx.stroke();

        yPosition += 50;
        const confidenceY = yPosition;
        
        // ML Prediction
        ctx.fillStyle = '#666666';
        ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('ML Prediction', 250, confidenceY);
        ctx.fillStyle = '#1a1a1a';
        ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.fillText(result.analysisData.ml_prediction || 'N/A', 250, confidenceY + 25);
        ctx.fillStyle = '#666666';
        ctx.font = '15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.fillText(`${Math.round((result.analysisData.ml_confidence || 0) * 100)}%`, 250, confidenceY + 45);

        // Correlation Match
        ctx.fillStyle = '#666666';
        ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.fillText('Correlation Match', 600, confidenceY);
        ctx.fillStyle = '#1a1a1a';
        ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.fillText(result.analysisData.corr_prediction || 'N/A', 600, confidenceY + 25);
        ctx.fillStyle = '#666666';
        ctx.font = '15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.fillText(`${Math.round((result.analysisData.corr_confidence || 0) * 100)}%`, 600, confidenceY + 45);

        // Combined Score
        ctx.fillStyle = '#666666';
        ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.fillText('Combined Score', 950, confidenceY);
        ctx.fillStyle = getConfidenceColor(result?.confidence || '0%');
        ctx.font = 'bold 15px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.fillText(`${Math.round((result.analysisData.predictionCert || 0) * 100)}%`, 950, confidenceY + 25);

        yPosition += 45;
      }

      yPosition += 50;

      // Spectral Analysis Section
      ctx.fillStyle = '#1a1a1a';
      ctx.font = 'bold 20px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText('Spectral Analysis', 40, yPosition);

      yPosition += 50;

      // High-resolution plot area
      const plotHeight = 1400;
      const plotWidth = BASE_WIDTH - 80;
      
      if (plotDataUrl) {
        const plotImg = new Image();
        plotImg.onload = function() {
          // Draw crisp plot border
          ctx.strokeStyle = '#e0e0e0';
          ctx.lineWidth = 1;
          ctx.strokeRect(40, yPosition, plotWidth, plotHeight);
          
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(41, yPosition + 1, plotWidth - 2, plotHeight - 2);
          
          // FORCE the plot to fill most of the available space
          const plotPadding = 20; // Small padding inside the border
          const drawWidth = plotWidth - (plotPadding * 2);  // Use almost full width
          const drawHeight = plotHeight - (plotPadding * 2); // Use almost full height
          const drawX = 40 + plotPadding;
          const drawY = yPosition + plotPadding;
          
          // Draw the plot image SCALED to fill the larger area
          ctx.drawImage(plotImg, drawX, drawY, drawWidth, drawHeight);
          
          finishExport();
        };
        plotImg.src = plotDataUrl;
      } else {
        finishExport();
      } 

      function finishExport() {
        yPosition += plotHeight + 20;

        // Footer with crisp text
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(40, yPosition);
        ctx.lineTo(BASE_WIDTH - 40, yPosition);
        ctx.stroke();

        yPosition += 20;
        ctx.fillStyle = '#666666';
        ctx.font = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", Arial, sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('Generated by Plastic Analytics - Advanced Polymer Identification', BASE_WIDTH / 2, yPosition);
        ctx.fillText('Visit us at plasticanalytics.co.uk for more information', BASE_WIDTH / 2, yPosition + 25);

        // Export at maximum quality
        const finalDataUrl = canvas.toDataURL('image/png', 1.0);
        
        toast.dismiss('exporting-plot');

        const link = document.createElement('a');
        link.href = finalDataUrl;
        link.download = `plastic-analysis-${result?.polymer || 'result'}-${result?.id || Date.now()}-HD.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success('High-resolution export completed!', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
      }

    } catch (error) {
      toast.dismiss('exporting-plot');
      console.error('Error generating high-res export:', error);
      toast.error(`Export failed: ${error.message}`, {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 5000,
      });
    }
  };
  

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const isDesktop = windowWidth >= 1024;
  const isSmallScreen = windowWidth < 1024;

  // 3. Add this function to handle filter changes
const handleFilterChange = (column, value) => {
  trackFilterApplied(column, value);
  setFilters(prev => ({
    ...prev,
    [column]: value
  }));
  setShowFilterMenu(null);
};

const handleViewResultInPlot = (resultItem) => {
  trackResultViewed(resultItem.id, resultItem.polymer, resultItem.confidence);
  // console.log('resultItem', resultItem);
  
  // Check if we have the detailed analysis data
  const analysisData = resultItem.analysisData || {};
  // console.log('analysisData', analysisData);

  
  setPlotConfig({
    url: resultItem.fileLocation,
    polymer: analysisData.best_match || resultItem.polymer, // Use stored best_match or fallback
    instrument: resultItem.instrument || formInstrument, // Use stored instrument
    variable: resultItem.variable || formVariable, // Use stored variable
    confidences: {
      predictionCert: analysisData.predictionCert,
      corr_confidence: analysisData.corr_confidence,
      ml_confidence: analysisData.ml_confidence
    },
    // Additional data you might want to pass to PlotData
    predictions: {
      ml_prediction: analysisData.ml_prediction,
      corr_prediction: analysisData.corr_prediction
    },
    key: resultItem.id,
    match: analysisData.best_match || resultItem.polymer
  });
  
  // Optional - scroll to the plot section if it's not in view
  const plotSection = document.querySelector('#plot-section');
  if (plotSection) {
    plotSection.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }
};


// 4. Add this function to clear a filter
const clearFilter = (column) => {
  trackFilterCleared(column);
  setFilters(prev => {
    const newFilters = { ...prev };
    delete newFilters[column];
    return newFilters;
  });
  setShowFilterMenu(null);
};

// 5. Add this function to clear all filters
const clearAllFilters = () => {
  setFilters({});
};

// 6. Add this function to apply filters to results
const getFilteredResults = () => {
  const resultsToFilter = sessionResults.length > 0 ? sessionResults : analysisResults;
  
  if (Object.keys(filters).length === 0) {
    return resultsToFilter;
  }
  
  return resultsToFilter.filter(result => {
    return Object.entries(filters).every(([column, value]) => {
      if (!value) return true;
      
      switch (column) {
        case 'polymer':
          return result.polymer.toLowerCase() === value.toLowerCase();
        case 'classification':
          return result.classification.toLowerCase() === value.toLowerCase();
        case 'confidence':
          const confidenceValue = parseInt(result.confidence);
          if (value === 'high') return confidenceValue >= 80;
          if (value === 'medium') return confidenceValue >= 70 && confidenceValue < 80;
          if (value === 'low') return confidenceValue < 70;
          return true;
        default:
          return true;
      }
    });
  });
};
const handleShowAnalysisForm = () => {
  trackAnalysisFormShow();
  setShowAnalysisForm(true);
  
  // Use setTimeout to ensure the form is rendered before scrolling
  setTimeout(() => {
    const formElement = document.getElementById('analysis-form');
    if (formElement) {
      formElement.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest' 
      });
    }
  }, 100); // Small delay to ensure DOM is updated
};

// 7. Define FilterMenu component
const FilterMenu = ({ column, onFilter, onClear }) => {
  const filterOptions = getFilterOptions(column);
  
  return (
    <div 
      ref={filterMenuRef}
      style={{
        position: 'absolute',
        top: '100%',
        right: 0,
        zIndex: 20,
        backgroundColor: 'white',
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        width: '200px',
        padding: '8px',
        marginTop: '4px'
      }}
    >
      <div style={{ fontSize: '14px', fontWeight: '500', marginBottom: '8px', padding: '4px 0' }}>
        Filter by {column}
      </div>
      <div style={{ 
        maxHeight: '200px', 
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px'
      }}>
        {filterOptions.map(option => (
          <div 
            key={option.value}
            style={{ 
              padding: '6px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '13px',
              backgroundColor: filters[column] === option.value ? '#f0f0f0' : 'transparent',
              ":hover": { backgroundColor: '#f5f5f5' }
            }}
            onClick={() => onFilter(column, option.value)}
          >
            {option.label}
          </div>
        ))}
      </div>
      <div style={{ 
        borderTop: '1px solid #e0e0e0',
        marginTop: '8px',
        paddingTop: '8px',
        display: 'flex',
        justifyContent: 'space-between'
      }}>
        <button 
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#4F46E5',
            cursor: 'pointer'
          }}
          onClick={() => onClear(column)}
        >
          Clear Filter
        </button>
        <button 
          style={{
            padding: '4px 8px',
            fontSize: '12px',
            border: 'none',
            backgroundColor: 'transparent',
            color: '#4F46E5',
            cursor: 'pointer'
          }}
          onClick={() => setShowFilterMenu(null)}
        >
          Close
        </button>
      </div>
    </div>
  );
};

// 8. Function to get filter options based on column
const getFilterOptions = (column) => {
  switch (column) {
    case 'polymer':
      return [
        { label: 'Polyamide', value: 'Polyamide' },
        { label: 'Polyethylene', value: 'Polyethylene' },
        { label: 'Polyethylene terephthalate', value: 'Polyethylene terephthalate' },
        { label: 'Polypropylene', value: 'Polypropylene' },
        { label: 'Polystyrene', value: 'Polystyrene' },
        { label: 'Polyvinyl chloride', value: 'Polyvinyl chloride' },
        { label: 'Polybutylene terephthalate', value: 'Polybutylene terephthalate' },
        { label: 'Polychloroprene', value: 'Polychloroprene' },
        { label: 'Polybutadiene acrylonitrile', value: 'Polybutadiene acrylonitrile' },
        { label: 'Styrene acrylonitrile', value: 'Styrene acrylonitrile' },
        { label: 'Polycarbonate', value: 'Polycarbonate' },
        { label: 'Polylactic acid', value: 'Polylactic acid' },
        { label: 'Ethylene vinyl acetate', value: 'Ethylene vinyl acetate' },
        { label: 'Polymethyl methacrylate', value: 'Polymethyl methacrylate' },
        { label: 'Ethylene vinyl alcohol', value: 'Ethylene vinyl alcohol' },
        { label: 'Acrylonitrile butadiene styrene', value: 'Acrylonitrile butadiene styrene' },
        { label: 'Ethylene propylene', value: 'Ethylene propylene' },
        { label: 'Polyurethane', value: 'Polyurethane' },
        { label: 'Other', value: 'Other' }
      ];
    case 'classification':
      return [
        { label: 'Fiber', value: 'fiber' },
        { label: 'Fragment', value: 'fragment' },
        { label: 'Film', value: 'film' },
        { label: 'Foam', value: 'foam' },
        { label: 'Pellet', value: 'pellet' }
      ];
    case 'confidence':
      return [
        { label: 'High (≥80%)', value: 'high' },
        { label: 'Medium (70-79%)', value: 'medium' },
        { label: 'Low (<70%)', value: 'low' }
      ];
    default:
      return [];
  }
};

// 9. Create a component for the filter-enabled header cell
const FilterableHeaderCell = ({ column, label, minWidth }) => {
  const hasActiveFilter = filters[column];
  
  return (
    <th style={{ 
      ...styles.th, 
      minWidth,
      position: 'relative',
      cursor: 'pointer'
    }}>
      <div 
        style={{ 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
        onClick={() => setShowFilterMenu(showFilterMenu === column ? null : column)}
      >
        <span>{label}</span>
        <span style={{ 
          marginLeft: '4px',
          color: hasActiveFilter ? '#4F46E5' : '#aaa',
          display: 'flex',
          alignItems: 'center'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={hasActiveFilter ? "#4F46E5" : "currentColor"} strokeWidth="2">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
          </svg>
        </span>
      </div>
      {showFilterMenu === column && (
        <FilterMenu 
          column={column} 
          onFilter={handleFilterChange} 
          onClear={clearFilter} 
        />
      )}
    </th>
  );
};

  const polymer_colors = {
    "Polyamide": {"bg": "#E0FFFF", "text": "#008888"},
    "Polyethylene": {"bg": "#E0FFE0", "text": "#006600"},
    "Polyethylene terephthalate": {"bg": "#FFE0E0", "text": "#CC0000"},
    "Polypropylene": {"bg": "#E0E0FF", "text": "#0000CC"},
    "Polystyrene": {"bg": "#FFE0FF", "text": "#CC00CC"},
    "Polyvinyl chloride": {"bg": "#FFFFE0", "text": "#999900"},
    "Polybutylene terephthalate": {"bg": "#FFF0E0", "text": "#CC6600"},
    "Polychloroprene": {"bg": "#F0E0FF", "text": "#6600CC"},
    "Polybutadiene acrylonitrile": {"bg": "#E0F0FF", "text": "#0066CC"},
    "Styrene acrylonitrile": {"bg": "#FFE0F0", "text": "#CC0066"},
    "Polycarbonate": {"bg": "#FFE0E6", "text": "#CC4444"},
    "Polylactic acid": {"bg": "#E0FFE6", "text": "#00AA44"},
    "Ethylene vinyl acetate": {"bg": "#E6E0FF", "text": "#4400CC"},
    "Polymethyl methacrylate": {"bg": "#E6FFE0", "text": "#44CC44"},
    "Ethylene vinyl alcohol": {"bg": "#FFE6E0", "text": "#CC4400"},
    "Acrylonitrile butadiene styrene": {"bg": "#E0E6FF", "text": "#4444CC"},
    "Ethylene propylene": {"bg": "#F0FFE0", "text": "#66AA00"},
    "Polyurethane": {"bg": "#E0E6FF", "text": "#4444CC"},
    "Polyamide": {"bg": "#E0FFFF", "text": "#008888"},
    "Polyethylene": {"bg": "#E0FFE0", "text": "#006600"},
    "Polyethylene terephthalate": {"bg": "#FFE0E0", "text": "#CC0000"},
    "Polypropylene": {"bg": "#E0E0FF", "text": "#0000CC"},
    "Polystyrene": {"bg": "#FFE0FF", "text": "#CC00CC"},
    "Polyvinyl chloride": {"bg": "#FFFFE0", "text": "#999900"},
    "Polybutylene terephthalate": {"bg": "#FFF0E0", "text": "#CC6600"},
    "Polychloroprene": {"bg": "#F0E0FF", "text": "#6600CC"},
    "Polybutadiene acrylonitrile": {"bg": "#E0F0FF", "text": "#0066CC"},
    "Styrene acrylonitrile": {"bg": "#FFE0F0", "text": "#CC0066"},
    "Polycarbonate": {"bg": "#FFE0E6", "text": "#CC4444"},
    "Polylactic acid": {"bg": "#E0FFE6", "text": "#00AA44"},
    "Ethylene vinyl acetate": {"bg": "#E6E0FF", "text": "#4400CC"},
    "Polymethyl methacrylate": {"bg": "#E6FFE0", "text": "#44CC44"},
    "Ethylene vinyl alcohol": {"bg": "#FFE6E0", "text": "#CC4400"},
    "Acrylonitrile butadiene styrene": {"bg": "#E0E6FF", "text": "#4444CC"},
    "Ethylene propylene": {"bg": "#F0FFE0", "text": "#66AA00"},
    "Polyurethane": {"bg": "#E0E6FF", "text": "#4444CC"},
    "Other": {"bg": "#E6E6E6", "text": "#666666"},
  
    // New polymers from the list
    "AcryIonitrile-styrene-acrylate": {"bg": "#FFE8E8", "text": "#CC2222"},
    "Methacrylate-butadiene-styrene": {"bg": "#E8FFE8", "text": "#22CC22"},
    "Methyl cellulose": {"bg": "#E8E8FF", "text": "#2222CC"},
    "Hydroxypropyl-methyl-cellulose": {"bg": "#FFE8FF", "text": "#CC22CC"},
    "Hydroxypropyl-cellulose": {"bg": "#FFFFE8", "text": "#CCCC22"},
    "Hydroxyethyl-cellulose": {"bg": "#E8FFFF", "text": "#22CCCC"},
    "Yak fur bleached": {"bg": "#F0F0E8", "text": "#808040"},
    "Yak fur": {"bg": "#E8F0F0", "text": "#408080"},
    "Wild boar fur": {"bg": "#F0E8F0", "text": "#804080"},
    "Alkyd varnish": {"bg": "#F0F0F0", "text": "#606060"},
    "Polyacrylic acid": {"bg": "#FFE4E4", "text": "#CC1111"},
    "Methyl methacrylate acrylonitrile-butadiene-styrene": {"bg": "#E4FFE4", "text": "#11CC11"},
    "Acrylonitrile butadiene": {"bg": "#E4E4FF", "text": "#1111CC"},
    "Polyimide": {"bg": "#FFE4FF", "text": "#CC11CC"},
    "Olefinic thermoplastic elastomer": {"bg": "#FFFFE4", "text": "#CCCC11"},
    "Phenol-formaldehyde": {"bg": "#E4FFFF", "text": "#11CCCC"},
    "Poly(butylene terephthalate)": {"bg": "#F4E8E8", "text": "#884444"},
    "Poly(cyclohexylene dimethylene cyclohexanedicarboxylate), glycol and acid comonomer": {"bg": "#E8F4E8", "text": "#448844"},
    "Poly(cyclohexylene dimethylene terephthalate), acid": {"bg": "#E8E8F4", "text": "#444488"},
    "Poly(cyclohexylene dimethylene terephthalate), glycol": {"bg": "#F4E8F4", "text": "#884488"},
    "Poly(ethylene terephthalate), glycol": {"bg": "#F4F4E8", "text": "#888844"},
    "Poly(oxymethylene)": {"bg": "#E8F4F4", "text": "#448888"},
    "Polyformaldehyde": {"bg": "#F8E8E8", "text": "#AA3333"},
    "Poly(phenylene ether)": {"bg": "#E8F8E8", "text": "#33AA33"},
    "Poly(phenylene sulfide)": {"bg": "#E8E8F8", "text": "#3333AA"},
    "Poly(phenylene sulfone)": {"bg": "#F8E8F8", "text": "#AA33AA"},
    "Poly(vinylidene fluoride)": {"bg": "#F8F8E8", "text": "#AAAA33"},
    "Polyamide 11 (Nylon11)": {"bg": "#E8F8F8", "text": "#33AAAA"},
    "Cellulose": {"bg": "#F0E4E4", "text": "#881111"},
    "Polyamide 12 (Nylon12)": {"bg": "#E4F0E4", "text": "#118811"},
    "Polyamide 46 (Nylon46)": {"bg": "#E4E4F0", "text": "#111188"},
    "Polyamide 6 (Nylon6)": {"bg": "#F0E4F0", "text": "#881188"},
    "Polyamide 6,10 (Nylon6-10)": {"bg": "#F0F0E4", "text": "#888811"},
    "Polyamide 6,12 (Nylon6-12)": {"bg": "#E4F0F0", "text": "#118888"},
    "Polyamide 6,6 (Nylon6-6)": {"bg": "#FCE8E8", "text": "#BB2222"},
    "Polyamide 6,6,6": {"bg": "#E8FCE8", "text": "#22BB22"},
    "Polyamide 66/6T": {"bg": "#E8E8FC", "text": "#2222BB"},
    "Polyamide thermoplastic elastomer": {"bg": "#FCE8FC", "text": "#BB22BB"},
    "Polyaryletherketone": {"bg": "#FCFCE8", "text": "#BBBB22"},
    "Copolyester thermoplastic elastomer": {"bg": "#E8FCFC", "text": "#22BBBB"},
    "Polyetherimide": {"bg": "#F4E4E4", "text": "#992222"},
    "Polyethersulfone": {"bg": "#E4F4E4", "text": "#229922"},
    "Polyethylenenaphthalate": {"bg": "#E4E4F4", "text": "#222299"},
    "Polyketone": {"bg": "#F4E4F4", "text": "#992299"},
    "Polylactic glycolic acid": {"bg": "#F4F4E4", "text": "#999922"},
    "Polysulfone": {"bg": "#E4F4F4", "text": "#229999"},
    "Cycloolefincopolymer": {"bg": "#F8E4E4", "text": "#AA1111"},
    "Polytetrafluoroethylene; Teflon": {"bg": "#E4F8E4", "text": "#11AA11"},
    "Thermoplastic rubber vulcanisate": {"bg": "#E4E4F8", "text": "#1111AA"},
    "Urethane thermoplastic elastomer": {"bg": "#F8E4F8", "text": "#AA11AA"},
    "Polytetrafluoroethylene": {"bg": "#F8F8E4", "text": "#AAAA11"},
    "High-density polyethylene": {"bg": "#E4F8F8", "text": "#11AAAA"},
    "Low-density polyethylene": {"bg": "#F0E8E4", "text": "#883311"},
    "Polyacrylonitrile": {"bg": "#E8F0E4", "text": "#338811"},
    "Epoxide or epoxy resin": {"bg": "#E4E8F0", "text": "#113388"},
    "Polyamide 6,9 (Nylon6,9)": {"bg": "#F0E4E8", "text": "#881133"},
    "Polyvinyl acetate": {"bg": "#F0F0E8", "text": "#888833"},
    "Poly(ethylene-co-acrylic acid)": {"bg": "#E8F0F0", "text": "#338888"},
    "Polyacetal": {"bg": "#ECE4E4", "text": "#771111"},
    "Cellulose acetate butyrate": {"bg": "#E4ECE4", "text": "#117711"},
    "Polyvinyl pyrrolidone": {"bg": "#E4E4EC", "text": "#111177"},
    "Polyvinyl alcohol": {"bg": "#ECE4EC", "text": "#771177"},
    "Vinyl chloride-vinylidene chloride": {"bg": "#ECECE4", "text": "#777711"},
    "Vinylidene chloride acrylonitrile": {"bg": "#E4ECEC", "text": "#117777"},
    "Ethylene-vinyl acetate": {"bg": "#F6E4E4", "text": "#993311"},
    "Vinyl chloride vinyl acetate maleic acid": {"bg": "#E4F6E4", "text": "#339911"},
    "Vinyl chloride vinyl acetate hydroxypropyl acrylate": {"bg": "#E4E4F6", "text": "#113399"},
    "Vinyl chloride vinyl acetate": {"bg": "#F6E4F6", "text": "#993399"},
    "Styrene maleic anhydride partial methyl ester": {"bg": "#F6F6E4", "text": "#999933"},
    "Styrene maleic anhydride": {"bg": "#E4F6F6", "text": "#339999"},
    "Styrene isoprene": {"bg": "#E8E4E8", "text": "#661166"},
    "Styrene ethylene butylene": {"bg": "#E8E8E4", "text": "#666611"},
    "Styrene butyl methacrylate": {"bg": "#E4E8E8", "text": "#116666"},
    "Styrene butadiene": {"bg": "#F2E4E4", "text": "#881111"},
    "Styrene allyl alcohol": {"bg": "#E4F2E4", "text": "#118811"},
    "Melamine-formaldehyde": {"bg": "#E4E4F2", "text": "#111188"},
    "Polyphenylsulfone": {"bg": "#F2E4F2", "text": "#881188"},
    "Polyoxymethylene": {"bg": "#F2F2E4", "text": "#888811"},
    "Polycaprolactone": {"bg": "#E4F2F2", "text": "#118888"},
    "Polyamide 6,3 (Nylon 6,3)": {"bg": "#FAE4E4", "text": "#AA2222"},
    "Polyamide 6,2 (Nylon 6,2)": {"bg": "#E4FAE4", "text": "#22AA22"},
    "Polyacrylamide": {"bg": "#E4E4FA", "text": "#2222AA"},
    "Polyvinylidene fluoride": {"bg": "#FAE4FA", "text": "#AA22AA"},
    "Melamine-phenol-formaldehyde": {"bg": "#FAFAE4", "text": "#AAAA22"},
    "Polyvinyl stearate": {"bg": "#E4FAFA", "text": "#22AAAA"},
    "Poly 4-methyl 1-pentene": {"bg": "#EEE4E4", "text": "#772222"},
    "Poly-4,4-dipropoxy,2-2-diphenyl propane fumarate": {"bg": "#E4EEE4", "text": "#227722"},
    "Poly(2-hydroxyethyl methacrylate)": {"bg": "#E4E4EE", "text": "#222277"},
    "poly(2-,6-dimethyl-p-phenylene oxide)": {"bg": "#EEE4EE", "text": "#772277"},
    "Poly(2-,4-,6-tribromostyrene)": {"bg": "#EEEEE4", "text": "#777722"},
    "Poly(1-butene isotactic)": {"bg": "#E4EEEE", "text": "#227777"},
    "Phenoxy resin": {"bg": "#F4E8E4", "text": "#993322"},
    "Polyamide 6,6 (Nylon 6,6)": {"bg": "#E4F4E8", "text": "#229933"},
    "Polyamide 6,12 (Nylon 6,12)": {"bg": "#E8E4F4", "text": "#332299"},
    "Other": {"bg": "#E6E6E6", "text": "#666666"}
  };
  
  const getConfidenceColor = (confidence) => {
    const value = parseFloat(confidence.replace('%', ''));
    if (value >= 80) return '#22C55E'; // Green
    if (value >= 70) return '#F97316'; // Orange
    return '#EF4444'; // Red
  };

  const navigateToLogin = async () => {
    trackLoginPrompt('save_results');
    navigate('/sign-in')
  }
  
  const sampleData = {
    url: null,
    polymer: 'Sample Polymer',
    instrument: 'FTIR',
    variable: '800-1800'
  };

  const styles = {
    container: {
      display: 'grid',
      gridTemplateColumns: isDesktop ? '400px 1fr' : '1fr',
      gridTemplateRows: !isDesktop && !isMobile ? 'auto auto' : 'auto',
      gap: isTablet ? '5px' : '15px',
      padding: isTablet ? '16px' : '20px',
      height: 'auto',
      maxWidth: '100%',
      overflowX: 'visible', // Changed from 'hidden' to 'visible'
      minWidth: 0 // Important: allows grid items to shrink below their content size
    },
    mobileMessage: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      textAlign: 'center',
      height: 'calc(100vh - 150px)',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
    },
    mobileIcon: { 
      width: '80px', 
      height: '80px', 
      marginBottom: '1.5rem' },
    mobileTitle: { 
      fontSize: '1.5rem', 
      fontWeight: '600', 
      marginBottom: '1rem' },
    mobileText: { 
      fontSize: '1rem', 
      color: '#666', 
      marginBottom: '1.5rem', 
      maxWidth: '300px' },
    bentoBox: {
      background: 'white',
      borderRadius: '8px',
      padding: isTablet ? '16px' : windowWidth < 768 ? '12px' : '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      width: isSmallScreen ? '100%' :'75%',
      position: 'relative',
      overflow: 'hidden' // Important for nested scrollbars not to be clipped
    },

    bentoBoxSecondColumn: {
      background: 'white',
      borderRadius: '8px',
      padding: isTablet ? '16px' : windowWidth < 768 ? '12px' : '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      width: '100%', // Use 100% instead of fixed percentage
      maxWidth: '100%', // Ensure it doesn't exceed container
      position: 'relative',
      overflowX: 'auto',
      overflowY: 'hidden',
      minHeight: '250px',
      WebkitOverflowScrolling: 'touch',
      minWidth: 0 // Important: allows the container to shrink
    },
    boxTitle: {
      fontSize: isTablet ? '15px' : '16px',
      marginBottom: isTablet ? '12px' : '16px',
      color: '#1a1a1a'
    },
    folderItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '12px 0',
      borderBottom: '1px solid #eee',
      cursor: 'pointer'
    },
    metadataItem: {
      marginBottom: '8px',
      color: '#666'
    },
    tableContainer: { // Renamed for clarity and specificity to the results table
      position: 'relative', // For positioning the scroll indicator
      maxHeight: '300px',   // Or your desired max height for vertical scroll
      overflowY: 'auto',
      overflowX: 'auto',    // Enable horizontal scrolling
      width: '100%',        // Take full width of its parent bentoBox
      border: '1px solid #f0f0f0',
      borderRadius: '4px',
      WebkitOverflowScrolling: 'touch', // For smoother scrolling on touch devices
    },
    resultsTable: { // Specific style for the results table
      borderCollapse: 'collapse',
      tableLayout: 'fixed', // Important for minWidth on th/td to work correctly
      width: 'auto',        // Let the table be as wide as its content + minWidths
                             // Alternatively, set a large min-width e.g., '1200px'
                             // if you want to guarantee a certain overall width before scrolling
      minHeight: '250px'
    },
    th: {
      textAlign: 'left',
      padding: isTablet ? '10px 8px' : '12px',
      borderBottom: '1px solid #e0e0e0',
      background: 'white',
      position: 'sticky',   // Make headers sticky to the top on vertical scroll
      top: 0,
      zIndex: 10,            // Ensure headers are above table content
      fontSize: isTablet ? '13px' : '14px',
      whiteSpace: 'nowrap', // Prevent header text from wrapping
      minWidth: '150px',    // Default min-width for headers, can be overridden
    },
    td: {
      padding: isTablet ? '10px 8px' : '12px',
      borderBottom: '1px solid #e0e0e0',
      fontSize: isTablet ? '13px' : '14px',
      whiteSpace: 'nowrap', // Prevent cell content from wrapping
      minWidth: '150px',    // Ensure cells have a minimum width
      // overflow: 'hidden', // Optional: if you want to hide overflowing text
      // textOverflow: 'ellipsis', // Optional: show ellipsis for overflow
    },
    scrollIndicator: {
      position: 'absolute',
      bottom: '10px',
      right: '10px',
      backgroundColor: 'rgba(0,0,0,0.6)',
      color: 'white',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      zIndex: 15, // Above table content and headers
      pointerEvents: 'none', // So it doesn't interfere with scrolling
      opacity: 0.8,
      // You might want to conditionally display this based on whether scrolling is needed
      // For now, let's make it always visible for demonstration if isSmallScreen
      display: isSmallScreen ? 'block' : 'none', // Show on smaller screens
    },
    td: {
      padding: isTablet ? '10px 8px' : '12px',
      borderBottom: '1px solid #e0e0e0',
      fontSize: isTablet ? '13px' : '14px',
      whiteSpace: 'nowrap' 
    },
    button: {
      padding: '6px 12px',
      border: '1px solid #e0e0e0',
      borderRadius: '4px',
      background: 'white',
      cursor: 'pointer',
      fontSize: isTablet ? '13px' : '14px'
    },
    tabButton: {
      padding: isTablet ? '6px 12px' : '8px 16px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: isTablet ? '13px' : '14px'
    },
    primaryButton: {
      padding: isTablet ? '8px 16px' : '10px 20px',
      backgroundColor: '#4F46E5',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: isTablet ? '13px' : '14px'
    },
    secondaryButton: {
      padding: isTablet ? '8px 16px' : '10px 20px',
      backgroundColor: 'white',
      color: '#4F46E5',
      border: '1px solid #4F46E5',
      borderRadius: '4px',
      cursor: 'pointer',
      fontWeight: '500',
      marginLeft: isTablet ? '8px' : '12px',
      fontSize: isTablet ? '13px' : '14px'
    },
    formGroup: {
      marginBottom: '16px'
    },
    formLabel: {
      display: 'block',
      marginBottom: '8px',
      color: '#444',
      fontSize: isTablet ? '13px' : '14px'
    },
    fileInput: {
      marginBottom: '16px'
    },
    polymerPill: (polymerType) => ({
      display: 'inline-block',
      padding: isTablet ? '2px 8px' : '4px 12px',
      borderRadius: '16px',
      fontSize: isTablet ? '12px' : '14px',
      fontWeight: '500',
      backgroundColor: polymer_colors[polymerType]?.bg || polymer_colors["Other"]?.bg || '#E6E6E6', // Added fallback
      color: polymer_colors[polymerType]?.text || polymer_colors["Other"]?.text || '#666666', // Added fallback
      transition: 'all 0.2s ease'
    }),
    confidenceIndicator: (confidence) => ({
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: getConfidenceColor(confidence),
      fontWeight: '500',
      fontSize: isTablet ? '12px' : '14px'
    }),
    confidenceDot: (confidence) => ({
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      backgroundColor: getConfidenceColor(confidence)
    }),
    limitIndicator: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginLeft: isTablet ? '8px' : '16px',
      fontSize: isTablet ? '12px' : '14px',
      color: '#666'
    },
    limitBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2px 8px',
      borderRadius: '12px',
      backgroundColor: '#f0f0f0',
      fontSize: isTablet ? '11px' : '12px',
      fontWeight: '600'
    },
    premiumBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2px 8px',
      borderRadius: '12px',
      backgroundColor: '#fef3c7', 
      color: '#b45309', 
      fontSize: isTablet ? '11px' : '12px',
      fontWeight: '600'
    },
    bannerInfo: {
      padding: '12px 16px',
      backgroundColor: '#f0f7ff', 
      borderRadius: '6px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      fontSize: isTablet ? '13px' : '14px'
    },
    headerFlex: {
      display: 'flex', 
      justifyContent: 'space-between', 
      alignItems: 'center', 
      marginBottom: isTablet ? '12px' : '16px',
      flexWrap: isTablet ? 'wrap' : 'nowrap',
      gap: isTablet ? '8px' : '0',
    },
    buttonGroup: {
      display: 'flex',
      gap: isTablet ? '8px' : '12px',
      flexWrap: 'wrap',
      paddingRight: '10px'
    }, 
    plotPlaceholder: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center', // Vertically center content
      flexGrow: 1,
      minHeight: '300px', // Ensure enough height for animation + text
      padding: '20px',
      textAlign: 'center',
      backgroundColor: '#f9fafb',
      borderRadius: '8px',
      border: '1px dashed #e0e0e0', // Keep the dashed border
      color: '#666',
      fontSize: isTablet ? '14px' : '16px',
      overflow: 'hidden', // Prevent chart from overflowing its container during animation
      height: '75%'
    },
    plotPlaceholderIcon: {
      fontSize: '48px', // Adjust size as needed
      marginBottom: '16px',
      color: '#9ca3af', // Icon color
    },
    uploadBox: {
      border: '2px dashed #E5E7EB',
      borderRadius: '8px',
      padding: isTablet ? '20px' : '24px',
      textAlign: 'center',
      marginTop: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      backgroundColor: '#f9fafb'
    },
    uploadBoxActive: {
      borderColor: 'rgb(17, 29, 74)',
      backgroundColor: '#f8fafc'
    },
  };

  const metadataFields_display = [
    { label: 'Sample ID', value: '-' },
    { label: 'Collection Date', value: '-' },
    { label: 'Location', value: '-' },
    { label: 'Analyst', value: '-' },
    { label: 'Total Particles', value: '-' },
    { label: 'Analysis Status', value: '-' }
  ];

  const tabs = ['Correlation Match', 'AI Match'];

  const analysisResults = [
  ];
  
  const handleInstrumentNameChange = (value) => {
    setFormInstrument(value); // Use form-specific state
    trackAnalysisInstrumentSelection(value);
  };
  
  const handleInputChange = (value) => {
    setFormVariable(value); // Use form-specific state
    trackAnalysisVariableSelection(value, formInstrument);
  };
  
  const handleMetaDropNameChange = (value) => {
    setSelectedMetaDropName(value);
  };
  
  const handleMetadataInputChange = (fieldName) => {
    setMetadataFields({
      inputOneValue: fieldName.inputOneValue,
      inputTwoValue: fieldName.inputTwoValue,
      inputThreeValue: fieldName.inputThreeValue,
      inputFourValue: fieldName.inputFourValue,
    });
  };
  
  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      trackAnalysisFileUpload(e.target.files[0]);
    }
  };
  
  const handleAnalyzeClick = async () => {
    // Check if all required fields are filled
    if (
      selectedFile &&
      formInstrument &&
      formVariable
    ) {
      try {
        // Generate random file code
        let fileCode = generateRandomFileCode(8);
        setFileCode(fileCode);
        
        // Show loading toast
        toast.info('Analysing spectrum, please wait...', {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 5000,
          toastId: 'analyzing',
          style: {
            color: '#282c34',
          },
          progressStyle: {
            background: '#282c34',
          },
        });
        setSelectedInstrument(formInstrument); // Update plot state only here
        setVariableInputValue(formVariable);
        // Upload file to Firebase Storage
        const chemoRef = ref(fbStorage, `tempFiles/chemometrics/${fileCode}`);
        await uploadBytes(chemoRef, selectedFile);
                
        setShowRightColumn(true);
        // console.log('running analysis', formInstrument, formVariable, fileCode)
        
        // Call the Firebase function
        const instance = httpsCallable(fbFunctions, 'plastic_AI_categorisation');
        const response = await instance({
            fileLocation: `tempFiles/chemometrics/${fileCode}`,
            instrument: formInstrument,
            variable: formVariable
        });
        
        // Dismiss the analyzing toast
        toast.dismiss('analyzing');
        
        // Process response
        const result = response.data;
        console.log('result', result)
        setFileUrl(result.fileLocation);
        
        // Format the confidence value
        const predictionCert = result.predictionCert;
        const prediction = predictionCert * 100;
        const formattedResult = prediction.toFixed(2) + '%';
        
        setPredictionAcc(formattedResult);
        setPredictedPolymer(result.best_match);
        
        // Clean up temporary file
        console.log('chemoRef', chemoRef)
        // await deleteObject(chemoRef);
        console.log('result.fileLocation', result.fileLocation)
        
        // Get classification from metadata or use a default
        const classification = selectedMetaDropName || 'Fragment';
        
        // Create dimensions based on whether we have a Fiber (long, thin) or another shape
        let length = '120.0';
        let width = '20.0';
        if (classification.toLowerCase() === 'fiber') {
          length = (100 + Math.random() * 200).toFixed(1);
          width = (5 + Math.random() * 15).toFixed(1);
        } else if (classification.toLowerCase() === 'fragment') {
          length = (50 + Math.random() * 100).toFixed(1);
          width = (30 + Math.random() * 70).toFixed(1);
        } else if (classification.toLowerCase() === 'film') {
          length = (100 + Math.random() * 300).toFixed(1);
          width = (80 + Math.random() * 200).toFixed(1);
        }
        
        // If we have a result file URL, update its metadata
        if (result.fileLocation && result.fileLocation !== `tempFiles/chemometrics/${fileCode}`) {
          try {
            const fileRef = ref(fbStorage, result.fileLocation);
            
            // Only attempt to update metadata if we're authenticated
            if (window.userAuth && window.userAuth.currentUser) {
              const newMetadata = {
                customMetadata: {
                  'type': selectedMetaDropName || 'Sample',
                  'source': metadataFields.inputOneValue || '',
                  'colour': metadataFields.inputTwoValue || '',
                  'associatedPublication': metadataFields.inputThreeValue || '',
                  'description': metadataFields.inputFourValue || '',
                  'uploadedBy': window.userAuth.currentUser.email
                }
              };
              
              await updateMetadata(fileRef, newMetadata);
            }
          } catch (metadataError) {
            console.error('Error updating metadata:', metadataError);
            // Continue without failing the whole operation
          }
        }

        
        // Create new result object
        const newResult = {
          id: sessionResults.length + 1,
          polymer: result.best_match,
          confidence: formattedResult,
          classification: 'Coming soon',
          length: 'Coming soon',
          width: 'Coming soon',
          instrument: formInstrument, // Use form state
          variable: formVariable, // Use form state
          fileLocation: result.fileLocation,
          
          // Store ALL the analysis results for later use in plotting
          analysisData: {
            best_match: result.best_match,
            predictionCert: result.predictionCert,
            corr_confidence: result.corr_confidence,
            ml_confidence: result.ml_confidence,
            ml_prediction: result.ml_prediction,
            corr_prediction: result.corr_prediction,
            // Store any other properties you might need
            fullResult: result // Store the complete result object as backup
          },
          
          // Include additional data from the ML model (for display in table)
          additionalInfo: `ML: ${result.ml_prediction} (${Math.round(result.ml_confidence * 100)}%) | Corr: ${result.corr_prediction} (${Math.round(result.corr_confidence * 100)}%)`
        };
        
        // Add the new result to session results
        setSessionResults(prevResults => [...prevResults, newResult]);
        
        // If it's an AI Match, increment the counter
        if (activeTab === 'AI Match') {
          setAiMatchesUsed(prev => prev + 1);
        }
        
        // Automatically show the result in the plot
        setPlotConfig({
          url: result.fileLocation,
          polymer: result.best_match,
          instrument: formInstrument,
          variable: formVariable, // Keep this as a string (e.g., "785")
          confidences: {  // New separate property
            predictionCert: result.predictionCert,
            corr_confidence: result.corr_confidence,
            ml_confidence: result.ml_confidence
          },
          key: newResult.id,
          match: result.best_match
        });
        
        // Show success toast with the result
        toast.success(`Analysis complete! Identified ${result.best_match} with ${formattedResult} confidence`, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
        
        // Hide the analysis form after completion
        setFormInstrument('');
        setFormVariable('');
        setSelectedFile(null);
        setShowAnalysisForm(false);
        
      } catch (error) {
        // Dismiss the analyzing toast if it exists
        trackAnalysisError(error.message, 'processing', activeTab);
        toast.dismiss('analyzing');
        
        console.error('Error analyzing spectrum:', error);
        toast.error(`Error analyzing spectrum: ${error.message || 'Unknown error'}`, {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 5000,
        });
      }
    } else {
      // If not all required fields are filled, show an error message
      toast.error('Please fill in all required fields', {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
    }
  };
  
  const renderPolymerPill = (polymer) => (
    <div style={styles.polymerPill(polymer)}>
      {polymer}
    </div>
  );

  const renderConfidence = (confidence) => (
    <div style={styles.confidenceIndicator(confidence)}>
      <div style={styles.confidenceDot(confidence)} />
      {confidence}
    </div>
  );

  const MobileMessage = () => (
    <div style={styles.mobileMessage}>
      <svg 
        style={styles.mobileIcon}
        width="80" 
        height="80" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="#4F46E5" 
        strokeWidth="1.5"
        strokeLinecap="round" 
        strokeLinejoin="round"
      >
        <rect x="2" y="7" width="20" height="15" rx="2" ry="2"></rect>
        <polyline points="17 2 12 7 7 2"></polyline>
      </svg>
      <h2 style={styles.mobileTitle}>Desktop Experience Required</h2>
      <p style={styles.mobileText}>
        The Spectral Analysis tool requires a larger screen for full functionality. 
        Please switch to a tablet or desktop device.
      </p>
    </div>
  );
  MobileMessage.displayName = 'MobileMessage';

  const renderAnalysisForm = () => (
    <div id="analysis-form" style={{...styles.bentoBox, flex: '1'}}>
      <h3 style={styles.boxTitle}>New Analysis</h3>
      {activeTab === 'AI Match' && aiMatchesUsed >= 3 && (
        <div style={styles.bannerInfo}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <div>
            <strong>AI Match limit reached.</strong> You've used {aiMatchesUsed}/3 free AI matches today.
            <a href="#" style={{color: '#4F46E5', textDecoration: 'none', marginLeft: '4px'}}>
              Log in or upgrade
            </a> for unlimited analyses.
          </div>
        </div>
      )}
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Instrument Type</label>
        <InstrumentListInput onChange={handleInstrumentNameChange} />
        {formInstrument && ( 
          <div style={{ marginTop: '8px' }}>
            <span>Selected: <strong>{formInstrument}</strong></span>
          </div>
        )}
      </div>
      {formInstrument && (
        <div style={styles.formGroup}>
          <label style={styles.formLabel}>Instrument Variables</label>
          <InstrumentVariedInput
            instrumentType={formInstrument} 
            onChange={handleInputChange}
          />
        </div>
      )}
      <div style={styles.formGroup}>
        <label style={styles.formLabel}>Upload Spectrum File</label>
        <p style={{ fontSize: isTablet ? '13px' : '14px', color: '#666', marginBottom: '8px' }}>
          Please ensure your spectrum is in a tab-delimited text file with two columns:
          Wavelength and Intensity values.
        </p>
          <div 
            style={{
              ...styles.uploadBox,
              background: selectedFile ? '#F9FAFB' : 'white'
            }}
            onClick={() => document.getElementById('file-upload').click()}
          >
            <input 
              type="file" 
              onChange={handleFileChange}
              style={{ display: 'none' }}
              id="file-upload"
              accept=".csv,.txt,.jdx,.dx,.spc"
            />
            {!selectedFile ? (
              <div>
                <p>Click or drag file to upload</p>
                <p style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                  Supported formats: CSV, TXT, JDX, DX, SPC
                </p>
              </div>
            ) : (
              <div style={styles.selectedFileInfo}>
                <span style={styles.fileNameText}>{selectedFile.name}</span>
                <span>{(selectedFile.size / 1024).toFixed(1)} KB</span>
              </div>
            )}
          </div>
      </div>
      <div style={{ display: 'flex', marginTop: '20px', flexWrap: isTablet ? 'wrap' : 'nowrap', gap: isTablet ? '8px' : '0' }}>
        <Button
          type='primary'
          onClick={handleAnalyzeClick}
          disabled={!selectedFile || !formInstrument || !formVariable || (activeTab === 'AI Match' && aiMatchesUsed >= 3)} 
        >
          Run Analysis
        </Button>
        <Button
          type='secondary'
          onClick={() => {
            setShowAnalysisForm(false);
            // Optionally clear form state when canceling
            setFormInstrument('');
            setFormVariable('');
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
  renderAnalysisForm.displayName = 'renderAnalysisForm';

  if (isMobile) {
    return <MobileMessage />;
  }

  return (
    <div style={styles.container}>
      {/* Left Column */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: isTablet ? '16px' : '24px' }}>
        <div style={{ ...styles.bentoBox }}>
          <div style={styles.headerFlex}>
            <h3 style={styles.boxTitle}>Current Session</h3>
            <Button
            onClick={handleShowAnalysisForm} // Changed from setShowAnalysisForm(true)
            type='primary'
          >
            New Analysis
          </Button>
          </div>
          <div style={{ padding: isTablet ? '16px 0' : '20px 0', textAlign: 'center', color: '#666' }}>
            <p><strong>Your current session data</strong></p>
            <p style={{ fontSize: isTablet ? '13px' : '14px', marginTop: '8px' }}>
              You currently have {sessionResults.length} analysis results in this session.
              Results will be lost when you close the browser.
            </p>
            <Button 
             type='login'
              onClick={() => {navigateToLogin()}}
            >
              Log In to Save Results
            </Button>
          </div>
          {/*
          <div style={{ 
            marginTop: isTablet ? '12px' : '16px', 
            padding: isTablet ? '10px' : '12px', 
            backgroundColor: '#f9fafb', 
            borderRadius: '6px',
            display: 'flex',
            justifyContent: 'space-between',
            flexWrap: isTablet ? 'wrap' : 'nowrap'
          }}>
            <div>
              <span style={{ fontSize: isTablet ? '13px' : '14px', color: '#666' }}>AI Matches Used Today:</span>
              <div style={{ marginTop: '4px' }}>
                <span style={styles.limitBadge}>{aiMatchesUsed}/3</span>
                <span style={{ marginLeft: '8px', fontSize: isTablet ? '12px' : '13px', color: '#666' }}>
                  {3 - aiMatchesUsed} remaining
                </span>
              </div>
            </div>

            <div style={{
              ...styles.premiumBadge,
              marginTop: isTablet ? '8px' : '0',
              alignSelf: isTablet ? 'flex-start' : 'center'
            }}>
              Free Tier
            </div>
          </div>
          */}
        </div>

        {showAnalysisForm ? (
          renderAnalysisForm()
        ) : (
          <div style={styles.bentoBox}>
            <div style={styles.headerFlex}>
              <h3 style={styles.boxTitle}>Metadata Summary</h3>
            </div>
            <div style={{ padding: isTablet ? '10px' : '12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666', fontSize: isTablet ? '13px' : '14px' }}>Session Start:</span>
                <span style={{ fontSize: isTablet ? '13px' : '14px' }}>{new Date().toLocaleTimeString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ color: '#666', fontSize: isTablet ? '13px' : '14px' }}>Analyses Run:</span>
                <span style={{ fontSize: isTablet ? '13px' : '14px' }}>{sessionResults.length}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#666', fontSize: isTablet ? '13px' : '14px' }}>Data Storage:</span>
                <span style={{ fontSize: isTablet ? '13px' : '14px' }}>Temporary (Session Only)</span>
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: isTablet ? '10px' : '12px', marginTop: isTablet ? '12px' : '16px' }}>
              <p style={{ fontSize: isTablet ? '13px' : '14px', color: '#666' }}>
                Log in to access full metadata and save your analysis history
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Right Column - Session Results and Analysis Graph */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: isTablet ? '16px' : '24px', maxWidth: '1150px', minWidth: 0, }}>
        {/* Session Results Table Section */}
        <div style={styles.bentoBoxSecondColumn}>
          <div style={styles.headerFlex}>
            <h3 style={styles.boxTitle}>Session Results</h3>
            <div style={styles.buttonGroup}>
            {Object.keys(filters).length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', marginRight: '8px' }}>
                <span style={{ fontSize: '13px', color: '#666', marginRight: '8px' }}>
                  Filters: {Object.keys(filters).length}
                </span>
                <button 
                  style={{
                    ...styles.button,
                    fontSize: '12px',
                    padding: '4px 8px',
                  }}
                  onClick={clearAllFilters}
                >
                  Clear All
                </button>
              </div>
            )}
            <Button 
              type='secondary'
              style={styles.button} 
              onClick={handleExportToCSV}
              disabled={isExporting || (!sessionResults.length && !analysisResults.length)}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </div>
          </div>

          {/* Table container with styles for proper scrolling */}
          {/* Table container with styles for proper scrolling */}
          <div style={styles.tableContainer}>
            <table style={styles.resultsTable}>
              <thead>
                <tr>
                  <th style={{ ...styles.th, minWidth: '80px' }}>#</th>
                  <th style={{ ...styles.th, minWidth: '100px' }}>Image</th>
                  <FilterableHeaderCell column="polymer" label="Predicted Polymer" minWidth="180px" />
                  <FilterableHeaderCell column="confidence" label="Prediction confidence" minWidth="200px" />
                  <FilterableHeaderCell column="classification" label="Classification" minWidth="150px" />
                  <th style={{ ...styles.th, minWidth: '120px' }}>Length (&micro;m)</th>
                  <th style={{ ...styles.th, minWidth: '120px' }}>Width (&micro;m)</th>
                  <th style={{ ...styles.th, minWidth: '180px' }}>Additional Info</th>
                  <th style={{ ...styles.th, minWidth: '120px' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredResults().length > 0 ? (
                  getFilteredResults().map((result, index) => (
                    <tr 
                      key={result.id || `result-${index}`}
                      onClick={() => handleViewResultInPlot(result)}
                      style={{ 
                        ...styles.tr, 
                        cursor: 'pointer',
                        backgroundColor: plotConfig?.key === result.id ? 'rgb(241, 245, 249)' : 'transparent'
                      }}
                    >
                      {/* Your existing table row content */}
                      <td style={{ ...styles.td, minWidth: '80px' }}>{result.id}</td>
                      <td style={{ ...styles.td, minWidth: '100px' }}>
                        <div style={{ 
                          width: '60px', 
                          height: '40px', 
                          backgroundColor: '#f0f0f0', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          color: '#999', 
                          fontSize: '10px',
                          borderRadius: '4px',
                          border: '1px dashed #ccc'
                        }}>
                          Coming soon
                        </div>
                      </td>
                      <td style={{ ...styles.td, minWidth: '180px' }}>
                        {renderPolymerPill(result.polymer)}
                      </td>
                      <td style={{ ...styles.td, minWidth: '200px' }}>
                        {renderConfidence(result.confidence)}
                      </td>
                      <td style={{ ...styles.td, minWidth: '150px', color: '#999', fontStyle: 'italic' }}>
                        {result.classification}
                      </td>
                      <td style={{ ...styles.td, minWidth: '120px', color: '#999', fontStyle: 'italic' }}>
                        {result.length}
                      </td>
                      <td style={{ ...styles.td, minWidth: '120px', color: '#999', fontStyle: 'italic' }}>
                        {result.width}
                      </td>
                      <td style={{ ...styles.td, minWidth: '180px' }}>{result.additionalInfo || 'Sample data'}</td>
                      <td style={{ ...styles.td, minWidth: '120px' }}>Completed</td>
                    </tr>
                  ))
                ) : (
                  // Empty state message when no results
                  <tr>
                    <td 
                      colSpan="9" 
                      style={{
                        ...styles.td,
                        textAlign: 'center',
                        padding: '40px 20px',
                        color: '#666',
                        borderBottom: 'none'
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                        <svg 
                          width="48" 
                          height="48" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#9ca3af" 
                          strokeWidth="1.5"
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                        >
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                          <polyline points="14,2 14,8 20,8"></polyline>
                          <line x1="16" y1="13" x2="8" y2="13"></line>
                          <line x1="16" y1="17" x2="8" y2="17"></line>
                          <polyline points="10,9 9,9 8,9"></polyline>
                        </svg>
                        <div>
                          <div style={{ fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                            No analysis results yet
                          </div>
                          <div style={{ fontSize: '14px', color: '#999' }}>
                            Click "New Analysis" above to upload your first spectrum file and get started
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {/* Only show scroll indicator if there's actual content */}
            {getFilteredResults().length > 0 && (
              <div style={styles.scrollIndicator}>
                Scroll for more &rarr;
              </div>
            )}
          </div>
          </div>


        {/* Analysis Graph Section */}
        <div 
            id="plot-section"
            data-plot-container
            style={{...styles.bentoBoxSecondColumn, flexGrow: 1}}
          >
            <div style={styles.headerFlex}>
              <div style={{ display: 'flex', gap: isTablet ? '8px' : '12px', flexWrap: 'wrap' }}>
                {tabs.map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    style={{
                      ...styles.tabButton,
                      background: activeTab === tab ? '#f0f0f0' : 'transparent',
                      color: activeTab === tab ? '#1a1a1a' : '#666',
                      fontWeight: activeTab === tab ? 500 : 400
                    }}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              {activeTab === 'AI Match' && (
                <div style={styles.limitIndicator}>
                  {/*
                  <span>Daily Limit:</span>
                  <span style={styles.limitBadge}>{aiMatchesUsed}/3 used</span>
                    */}
                </div>
              )}
            </div>

            {/* Current view indicator */}
            {plotConfig && (
              <div style={{
              backgroundColor: 'rgb(241, 245, 249)', // Same background on wrapper
              borderRadius: '8px', // Slightly larger border radius
              padding: '10px', // This creates the extra blue space
              marginBottom: '12px',
            }}>
              <div style={{
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: isTablet ? '13px' : '14px',
                width: '100%',
                padding: '10px',
                boxSizing: 'border-box',
              }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div>
                    <span>Currently viewing: </span>
                    <strong style={{ marginLeft: '8px' }}>
                      {plotConfig.polymer} ({plotConfig.instrument}, {plotConfig.variable})
                    </strong>
                  </div>
                  {plotConfig.confidences && (
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      <span>ML: {Math.round((plotConfig.confidences.ml_confidence || 0) * 100)}%</span>
                      <span style={{ marginLeft: '12px' }}>
                        Corr: {Math.round((plotConfig.confidences.corr_confidence || 0) * 100)}%
                      </span>
                      <span style={{ marginLeft: '12px' }}>
                        Combined: {Math.round((plotConfig.confidences.predictionCert || 0) * 100)}%
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {plotConfig.key && (
                    <span style={{ fontSize: '13px', color: '#666' }}>
                      Result #{plotConfig.key}
                    </span>
                  )}
                  {/* Add export button here for current plot */}
                  <Button 
                    type='secondary'
                    onClick={() => {
                      // Find the current result from session or analysis results
                      const currentResult = [...sessionResults, ...analysisResults].find(r => r.id === plotConfig.key);
                      if (currentResult) {
                        handleDownloadPlotWithBranding(currentResult);
                      }
                    }}
                  >
                    Export Plot
                  </Button>
                </div>
              </div>
              </div>
            )}

            {activeTab === 'AI Match' && aiMatchesUsed >= 3 ? (
              <div style={{ padding: isTablet ? '30px' : '40px', textAlign: 'center', backgroundColor: '#f9fafb', borderRadius: '8px', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="1.5" style={{ margin: '0 auto 16px' }}>
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <h3 style={{ fontSize: isTablet ? '16px' : '18px', marginBottom: '8px' }}>AI Match Limit Reached</h3>
                <p style={{ color: '#666', marginBottom: '16px', fontSize: isTablet ? '13px' : '14px' }}>
                  You've used all 3 of your free AI matches for today
                </p>
                <button style={{ ...styles.primaryButton, marginTop: '8px' }} onClick={() => { /* Handle login navigation */ }}>
                  Log In for Unlimited Analyses
                </button>
              </div>
            ) : plotConfig === null ? (
              <div style={styles.plotPlaceholder}>
                <div style={{ height: '150px', width: 'clamp(200px, 80%, 400px)', marginBottom: '20px' }}>
                  <AnimatedLinePlaceholder />
                </div>
                <p>Your categorised spectra will be presented here.</p>
                <p style={{ marginTop: '8px', fontSize: isTablet ? '12px' : '13px' }}>
                  Run a new analysis to see the plot or click a row in the results table above.
                  Use the tabs above to switch between Correlation Match and AI Match predictions.
                </p>
              </div>
            ) : (
              <div 
                data-plot-area
                style={{ width: '100%', overflowX: 'auto', flexGrow: 1, display: 'flex' }}
              >
                <PlotData
                  key={plotConfig.key}
                  url={plotConfig.url}
                  pageName={'Analyse'}
                  match={plotConfig.polymer}
                  instrument={plotConfig.instrument}
                  variable={plotConfig.variable}
                  confidences={plotConfig.confidences}
                  predictions={plotConfig.predictions}
                />
              </div>
            )}
          </div>
    </div>
  </div>
)}

export default AnalyseTab;